import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading } from '@digdir/designsystemet-react';
import { BarChartIcon, HeartIcon, ShieldLockIcon } from '@navikt/aksel-icons';
import { Routes } from '../../enums/Routes';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useRouterMetadataContext } from '../../contexts/routerMetaDataContext';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { TeamSelect } from '../../components/TeamSelect';
import { useUserOnboardingQuery } from '../../hooks/queries/onboarding/useUserOnboarding';
import { GettingStartedPanel } from '../../pages/GettingStarted/GettingStartedPanel';
import classes from './DashboardPage.module.css';

const modules = [
  {
    key: 'psychologicalSafety',
    link: Routes.DashboardPsychologicalSafety,
    icon: ShieldLockIcon,
    requiredFeature: 'PSYCHOLOGICAL_SAFETY' as const,
  },
  {
    key: 'workEnvironment',
    link: Routes.DashboardWorkEnvironment,
    icon: HeartIcon,
    requiredFeature: 'WORK_ENVIRONMENT' as const,
  },
  {
    key: 'custom',
    link: Routes.DashboardCustomSurveys,
    icon: BarChartIcon,
    requiredFeature: 'CUSTOM_SURVEYS' as const,
  },
];

/** God morgen før kl. 10, god dag til kl. 18, ellers god kveld */
export const greetingKey = (hour: number) =>
  hour >= 5 && hour < 10 ? 'morning' : hour >= 10 && hour < 18 ? 'day' : 'evening';

export const DashboardPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasFeature, isLoading: isLoadingFeatures } = useFeatureFlag();
  const { isTeamSelectorInPage } = useRouterMetadataContext();
  const { data: userInfo } = useGetUserinfo();
  const { data: onboarding } = useUserOnboardingQuery(Boolean(userInfo));
  // «Kom i gang» lives here for the goal of seeing how the team is doing
  const gettingStarted = onboarding?.gettingStarted && onboarding.goal === 'TEAM_SAFETY' ? onboarding : null;
  const beforeFirstStep = Boolean(gettingStarted && !gettingStarted.firstStepDone);

  const enabledModules = modules.filter((module) => hasFeature(module.requiredFeature));

  // Redirect base dashboard to the first available feature, once we know which ones the tenant has
  if (location.pathname === Routes.Dashboard) {
    if (isLoadingFeatures) return null;
    const firstAvailableRoute = enabledModules[0]?.link || Routes.DashboardPsychologicalSafety;
    return <Navigate to={firstAvailableRoute} replace />;
  }

  const greeting = t(`dashboard.greeting.${greetingKey(new Date().getHours())}`);

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <div className={classes.heading}>
            <span className={classes.eyebrow}>
              {gettingStarted?.role
                ? [t(`gettingStarted.roles.${gettingStarted.role}.label`), gettingStarted.companyName].filter(Boolean).join(' · ')
                : t('dashboard.eyebrow')}
            </span>
            <Heading level={1} data-size="xl">
              {gettingStarted?.firstName
                ? t('gettingStarted.home.welcome', { name: gettingStarted.firstName })
                : userInfo?.nickname
                  ? `${greeting}, ${userInfo.nickname}`
                  : greeting}
            </Heading>
            {gettingStarted && (
              <p className={`ds-paragraph ${classes.lead}`} data-size="md">
                {t(beforeFirstStep ? 'gettingStarted.home.lead' : 'gettingStarted.home.leadDone')}
              </p>
            )}
          </div>
          {isTeamSelectorInPage && !beforeFirstStep && <TeamSelect />}
        </div>

        {enabledModules.length > 1 && !beforeFirstStep && (
          <nav aria-label={t('dashboard.modulesLabel')}>
            <ul className={classes.tabs}>
              {enabledModules.map(({ key, link, icon: Icon }) => (
                <li key={key}>
                  <NavLink to={link} className={classes.tab}>
                    <Icon aria-hidden className={classes.tabIcon} />
                    {t(`surveys.page.modules.${key}`)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
      <div className={classes.outlet}>
        {gettingStarted && (
          <div className={classes.gettingStarted}>
            <GettingStartedPanel onboarding={gettingStarted} />
          </div>
        )}
        {/* Before the first step there is no team, and nothing for the dashboard to show */}
        {!beforeFirstStep && <Outlet />}
      </div>
    </div>
  );
};
